import { Store, safeStorage } from './store';

export type Quality = 'low' | 'medium' | 'high';

export type Action =
  | 'forward'
  | 'back'
  | 'left'
  | 'right'
  | 'jump'
  | 'sprint'
  | 'interact'
  | 'prank'
  | 'special'
  | 'scoreboard'
  | 'chat';

export const ACTION_LABELS: Record<Action, string> = {
  forward: 'Avanzar',
  back: 'Retroceder',
  left: 'Izquierda',
  right: 'Derecha',
  jump: 'Saltar',
  sprint: 'Correr',
  interact: 'Interactuar',
  prank: 'Empujón (broma)',
  special: 'Habilidad especial',
  scoreboard: 'Información de la partida',
  chat: 'Abrir chat',
};

export const DEFAULT_BINDINGS: Record<Action, string> = {
  forward: 'KeyW',
  back: 'KeyS',
  left: 'KeyA',
  right: 'KeyD',
  jump: 'Space',
  sprint: 'ShiftLeft',
  interact: 'KeyE',
  prank: 'KeyF',
  special: 'KeyQ',
  scoreboard: 'Tab',
  chat: 'Enter',
};

export interface Settings {
  quality: Quality;
  shadows: boolean;
  renderScale: number;
  viewDistance: number;
  showFps: boolean;
  sensitivity: number;
  invertY: boolean;
  reduceEffects: boolean;
  uiScale: number;
  master: number;
  music: number;
  sfx: number;
  showOthersFaces: boolean;
  bindings: Record<Action, string>;
}

const detectQuality = (): Quality => {
  const mobile = /Android|iPhone|iPad|Mobile/i.test(navigator.userAgent);
  const cores = navigator.hardwareConcurrency ?? 4;
  if (mobile || cores <= 2) return 'low';
  return cores >= 8 ? 'high' : 'medium';
};

export const QUALITY_PRESETS: Record<Quality, Pick<Settings, 'shadows' | 'renderScale' | 'viewDistance'>> = {
  low: { shadows: false, renderScale: 0.75, viewDistance: 90 },
  medium: { shadows: true, renderScale: 1, viewDistance: 140 },
  high: { shadows: true, renderScale: 1, viewDistance: 200 },
};

function initial(): Settings {
  const q = detectQuality();
  const base: Settings = {
    quality: q,
    ...QUALITY_PRESETS[q],
    showFps: false,
    sensitivity: 1,
    invertY: false,
    reduceEffects: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false,
    uiScale: 1,
    master: 0.8,
    music: 0.5,
    sfx: 0.8,
    showOthersFaces: true,
    bindings: { ...DEFAULT_BINDINGS },
  };
  try {
    const saved = JSON.parse(safeStorage.get('kokun.settings') ?? '{}');
    return { ...base, ...saved, bindings: { ...DEFAULT_BINDINGS, ...(saved.bindings ?? {}) } };
  } catch {
    return base;
  }
}

export const settings = new Store<Settings>(initial());
settings.subscribe((s) => {
  safeStorage.set('kokun.settings', JSON.stringify(s));
  document.documentElement.style.setProperty('--ui-scale', String(s.uiScale));
  document.documentElement.classList.toggle('reduce-fx', s.reduceEffects);
});
document.documentElement.style.setProperty('--ui-scale', String(settings.get().uiScale));
document.documentElement.classList.toggle('reduce-fx', settings.get().reduceEffects);

export function keyLabel(code: string): string {
  if (code.startsWith('Key')) return code.slice(3);
  if (code.startsWith('Digit')) return code.slice(5);
  const map: Record<string, string> = {
    Space: 'Espacio',
    ShiftLeft: 'Shift',
    ShiftRight: 'Shift D',
    ControlLeft: 'Ctrl',
    Tab: 'Tab',
    Enter: 'Intro',
    ArrowUp: '↑',
    ArrowDown: '↓',
    ArrowLeft: '←',
    ArrowRight: '→',
  };
  return map[code] ?? code;
}
